import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { TiTick } from "react-icons/ti";
import { RiAddCircleLine, RiFileList3Line } from "react-icons/ri";

import TableLoader from "../../../../utils/TableLoader";
import NewWinesCatalog from "./NewWinesCatalog";
import ExistingWinesOrder from "./ExistingWinesOrder";
import QuantityInfoTooltip from "./QuantityInfoTooltip";

const EditProposal = (props) => {
    const { proposalId: propId, onClose, onSaveSuccess, hasPermission } = props || {};
    const { slug2 } = useParams();
    const navigate = useNavigate();
    
    const activeProposalId = propId || slug2;
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");

    // Tab State: "new_wines" (Nuovi Vini) or "existing_wines" (Vini Esistenti)
    const [activeTab, setActiveTab] = useState("new_wines");

    // Loading States
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isWinesLoading, setIsWinesLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Proposal Data
    const [proposal, setProposal] = useState(null);
    const [existingWines, setExistingWines] = useState([]);

    // Wine Catalog Data (Scegli i vini)
    const [allWinesData, setAllWinesData] = useState([]);
    const [catalogPage, setCatalogPage] = useState(1);
    const [catalogRowsPerPage, setCatalogRowsPerPage] = useState(10);
    const [totalCatalogWines, setTotalCatalogWines] = useState(0);

    // Filter Options
    const [nations, setNations] = useState([]);
    const [regions, setRegions] = useState([]);
    const [grapeVarieties, setGrapeVarieties] = useState([]);
    const [productTypes, setProductTypes] = useState([]);

    // Selected Filters (Arrays for Multiple Selection)
    const [selectedNation, setSelectedNation] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState([]);
    const [selectedGrape, setSelectedGrape] = useState([]);
    const [selectedProductType, setSelectedProductType] = useState([]);

    // Handlers for toggling multi-select filter options
    const handleNationFilter = (item) => {
        const next = selectedNation.includes(item)
            ? selectedNation.filter((i) => i !== item)
            : [...selectedNation, item];
        setSelectedNation(next);

        const hasRemainingFilters =
            next.length > 0 ||
            selectedRegion.length > 0 ||
            selectedGrape.length > 0 ||
            selectedProductType.length > 0 ||
            (searchNome && searchNome.trim() !== "");

        if (!hasRemainingFilters) {
            setAllWinesData([]);
            setTotalCatalogWines(0);
        }
    };

    const handleRegionFilter = (item) => {
        const next = selectedRegion.includes(item)
            ? selectedRegion.filter((i) => i !== item)
            : [...selectedRegion, item];
        setSelectedRegion(next);

        const hasRemainingFilters =
            selectedNation.length > 0 ||
            next.length > 0 ||
            selectedGrape.length > 0 ||
            selectedProductType.length > 0 ||
            (searchNome && searchNome.trim() !== "");

        if (!hasRemainingFilters) {
            setAllWinesData([]);
            setTotalCatalogWines(0);
        }
    };

    const handleGrapeFilter = (item) => {
        const next = selectedGrape.includes(item)
            ? selectedGrape.filter((i) => i !== item)
            : [...selectedGrape, item];
        setSelectedGrape(next);

        const hasRemainingFilters =
            selectedNation.length > 0 ||
            selectedRegion.length > 0 ||
            next.length > 0 ||
            selectedProductType.length > 0 ||
            (searchNome && searchNome.trim() !== "");

        if (!hasRemainingFilters) {
            setAllWinesData([]);
            setTotalCatalogWines(0);
        }
    };

    const handleProductTypeFilter = (item) => {
        const next = selectedProductType.includes(item)
            ? selectedProductType.filter((i) => i !== item)
            : [...selectedProductType, item];
        setSelectedProductType(next);

        const hasRemainingFilters =
            selectedNation.length > 0 ||
            selectedRegion.length > 0 ||
            selectedGrape.length > 0 ||
            next.length > 0 ||
            (searchNome && searchNome.trim() !== "");

        if (!hasRemainingFilters) {
            setAllWinesData([]);
            setTotalCatalogWines(0);
        }
    };

    // Search Fields
    const [searchNome, setSearchNome] = useState("");

    // Catalog Batch Checkbox Selection
    const [selectedCatalogWineIds, setSelectedCatalogWineIds] = useState([]);
    const [newlySelectedWines, setNewlySelectedWines] = useState([]);

    // Existing wine IDs currently in the proposal
    // If an existing wine is removed from the proposal, it can be re-added as a new wine
    const existingWineIds = (existingWines || [])
        .map((sw) => sw.product_id || sw.wine?.id)
        .filter(Boolean);

    // Helper to get initial quantity for a wine
    const getInitialWineQuantity = (wine) => {
        // 1. If previously in proposal, preserve that quantity
        const inProposal = proposal?.proposal_wines?.find(
            (pw) => String(pw.product_id || pw.wine?.id) === String(wine?.id)
        );
        if (inProposal?.quantity) {
            const propQty = parseInt(inProposal.quantity, 10);
            if (!isNaN(propQty) && propQty > 0) return propQty;
        }

        // 2. Direct wine.quantity from API (e.g. 10)
        if (wine?.quantity !== undefined && wine?.quantity !== null) {
            const wineQty = parseInt(wine.quantity, 10);
            if (!isNaN(wineQty) && wineQty > 0) return wineQty;
        }

        // 3. Check wine.box_content (e.g. 6 or 12)
        if (wine?.box_content) {
            const boxQty = parseInt(wine.box_content, 10);
            if (!isNaN(boxQty) && boxQty > 0) return boxQty;
        }

        // 4. Check wine.meta[0]?.quantity
        if (wine?.meta && Array.isArray(wine.meta) && wine.meta.length > 0) {
            const metaQty = parseInt(wine.meta[0]?.quantity, 10);
            if (!isNaN(metaQty) && metaQty > 0) return metaQty;
        }

        // 5. Default fallback
        return 1;
    };

    // Helper to get initial price for a wine
    const getInitialWinePrice = (wine) => {
        if (wine?.meta && Array.isArray(wine.meta) && wine.meta.length > 0) {
            const metaWithPrice = wine.meta.find((m) => Number(m?.b2b_price) > 0);
            if (metaWithPrice) return metaWithPrice.b2b_price;
            if (wine.meta[0]?.b2b_price !== undefined) return wine.meta[0].b2b_price;
        }
        if (Number(wine?.b2b_price) > 0) return wine.b2b_price;
        if (Number(wine?.price) > 0) return wine.price;
        return wine?.b2b_price || wine?.price || 0;
    };

    const handleToggleSelectCatalogWine = (wine) => {
        const wineId = wine.id;
        const isExisting = existingWineIds.some((id) => String(id) === String(wineId));
        if (isExisting) {
            toast.info(`"${wine.name}" è già presente nella proposta.`, { theme: "colored" });
            return;
        }

        const isSelected = selectedCatalogWineIds.includes(wineId);

        if (isSelected) {
            setSelectedCatalogWineIds((prev) => prev.filter((id) => id !== wineId));
            setNewlySelectedWines((prev) => prev.filter((item) => item.product_id !== wineId));
        } else {
            setSelectedCatalogWineIds((prev) => [...prev, wineId]);
            const newWineItem = {
                product_id: wineId,
                quantity: getInitialWineQuantity(wine),
                wine: wine,
                b2b_price_snapshot: getInitialWinePrice(wine)
            };
            setNewlySelectedWines((prev) => {
                if (prev.some((item) => item.product_id === wineId)) return prev;
                return [...prev, newWineItem];
            });
        }
    };

    const handleToggleSelectAllCatalog = () => {
        const selectableWines = allWinesData.filter(
            (w) => !existingWineIds.some((id) => String(id) === String(w.id))
        );
        const selectableIds = selectableWines.map((w) => w.id);
        const allSelectableChecked = selectableIds.length > 0 && selectableIds.every((id) => selectedCatalogWineIds.includes(id));

        if (allSelectableChecked) {
            setSelectedCatalogWineIds((prev) => prev.filter((id) => !selectableIds.includes(id)));
            setNewlySelectedWines((prev) => prev.filter((item) => !selectableIds.includes(item.product_id)));
        } else {
            setSelectedCatalogWineIds((prev) => Array.from(new Set([...prev, ...selectableIds])));
            const newItems = selectableWines.map((wine) => ({
                product_id: wine.id,
                quantity: getInitialWineQuantity(wine),
                wine: wine,
                b2b_price_snapshot: getInitialWinePrice(wine)
            }));
            setNewlySelectedWines((prev) => {
                const updated = [...prev];
                newItems.forEach((item) => {
                    if (!updated.some((u) => u.product_id === item.product_id)) {
                        updated.push(item);
                    }
                });
                return updated;
            });
        }
    };

    // Dropdown open states
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRef = useRef(null);

    // 1. Fetch Proposal Details
    useEffect(() => {
        const fetchProposalDetails = async () => {
            if (!activeProposalId) return;
            setIsPageLoading(true);
            try {
                const response = await axios.get(`${apiUrl}/api/v1/proposals/${activeProposalId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = response.data;
                setProposal(data);

                // Map proposal wines to editable structure
                if (data?.proposal_wines && Array.isArray(data.proposal_wines)) {
                    const mappedWines = data.proposal_wines.map((pw) => ({
                        product_id: pw.product_id || pw.wine?.id,
                        quantity: pw.quantity ? Math.max(1, parseInt(pw.quantity, 10)) : 1,
                        wine: pw.wine || {},
                        b2b_price_snapshot: pw.b2b_price_snapshot || pw.wine?.b2b_price || 0
                    }));
                    setExistingWines(mappedWines);
                }
            } catch (error) {
                console.error("Error fetching proposal details:", error);
                toast.error("Errore nel caricamento del proposal.", { theme: "colored" });
            } finally {
                setIsPageLoading(false);
            }
        };

        fetchProposalDetails();
    }, [activeProposalId, apiUrl, token]);

    // 2. Fetch Filter Dropdown Options
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/v1/wineFilterOptions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = response.data;
                setNations(data?.nations || []);
                setRegions(data?.regions || []);
                setGrapeVarieties(data?.grape_varieties || []);
                setProductTypes(data?.product_types || []);
            } catch (error) {
                console.error("Error fetching wine filter options:", error);
            }
        };

        fetchFilterOptions();
    }, [apiUrl, token]);

    // 3. Fetch Catalog Wines ("Scegli i vini")
    const fetchCatalogWines = async (resetFilters = false) => {
        const hasActiveFilters =
            !resetFilters &&
            ((selectedNation && selectedNation.length > 0) ||
             (selectedRegion && selectedRegion.length > 0) ||
             (selectedGrape && selectedGrape.length > 0) ||
             (selectedProductType && selectedProductType.length > 0) ||
             (searchNome && searchNome.trim() !== ""));

        // Display catalog wines ONLY when a filter or search term is applied
        if (resetFilters || !hasActiveFilters) {
            setAllWinesData([]);
            setTotalCatalogWines(0);
            return;
        }

        setIsWinesLoading(true);
        try {
            const params = {
                page: catalogPage,
                per_page: catalogRowsPerPage,
                pageNumber: catalogPage,
                size: catalogRowsPerPage
            };

            if (selectedNation && selectedNation.length > 0) params.nation = selectedNation.join(",");
            if (selectedRegion && selectedRegion.length > 0) params.region = selectedRegion.join(",");
            if (selectedGrape && selectedGrape.length > 0) params.vitigno = selectedGrape.join(",");
            if (selectedProductType && selectedProductType.length > 0) params.product_type = selectedProductType.join(",");

            // Matching order module search parameter logic for API
            if (searchNome && searchNome.trim() !== "") {
                params.key = "name";
                params.search = searchNome.trim();
                params.name = searchNome.trim();
                params.wine = searchNome.trim();
            }

            const response = await axios.get(`${apiUrl}/api/v1/wines`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setAllWinesData(response.data?.data || []);
            setTotalCatalogWines(response.data?.total || 0);
        } catch (error) {
            console.error("Error fetching catalog wines:", error);
            setAllWinesData([]);
            setTotalCatalogWines(0);
        } finally {
            setIsWinesLoading(false);
        }
    };

    useEffect(() => {
        fetchCatalogWines();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogPage, catalogRowsPerPage]);

    // Auto-search on typing or clearing Nome (with 400ms debounce)
    useEffect(() => {
        const timer = setTimeout(() => {
            setCatalogPage(1);
            fetchCatalogWines();
        }, 400);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchNome]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest(".dropdown-container")) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [openDropdown]);

    // Filter Actions
    const handleApplyFilters = () => {
        setCatalogPage(1);
        fetchCatalogWines();
    };

    const handleResetFilters = () => {
        setSelectedNation([]);
        setSelectedRegion([]);
        setSelectedGrape([]);
        setSelectedProductType([]);
        setSearchNome("");
        setCatalogPage(1);
        fetchCatalogWines(true);
    };

    // Add wine from catalog to proposal order detail
    // Quantity Handlers for Existing Wines
    const handleQuantityChange = (index, delta) => {
        setExistingWines((prev) => {
            const updated = [...prev];
            const currentQty = updated[index]?.quantity || 1;
            const newQty = Math.max(1, currentQty + delta);
            updated[index].quantity = newQty;
            return updated;
        });
    };

    const handleDirectQuantityInput = (index, val) => {
        let sanitized = String(val).replace(/\D/g, "").replace(/^0+/, "");
        if (sanitized.length > 3) {
            sanitized = sanitized.slice(0, 3);
        }
        const newQty = sanitized === "" ? "" : parseInt(sanitized, 10);
        setExistingWines((prev) => {
            const updated = [...prev];
            if (updated[index]) {
                updated[index].quantity = newQty;
            }
            return updated;
        });
    };

    // Quantity Handlers for Newly Selected Catalog Wines
    const handleNewWineQuantityInput = (index, val) => {
        let sanitized = String(val).replace(/\D/g, "").replace(/^0+/, "");
        if (sanitized.length > 3) {
            sanitized = sanitized.slice(0, 3);
        }
        const newQty = sanitized === "" ? "" : parseInt(sanitized, 10);
        setNewlySelectedWines((prev) => {
            const updated = [...prev];
            if (updated[index]) {
                updated[index].quantity = newQty;
            }
            return updated;
        });
    };

    // Remove Handlers
    const handleRemoveWine = (index) => {
        if (!hasPermission?.("Proposals", "remove")) {
            toast.error("Non hai i permessi per eliminare questo vino.", { theme: "colored" });
            return;
        }
        if ((existingWines.length + newlySelectedWines.length) <= 1) {
            toast.warning("La proposal deve contenere almeno un vino.", { theme: "colored" });
            return;
        }
        setExistingWines((prev) => prev.filter((_, i) => i !== index));
        toast.info("Vino rimosso dalla proposal.", { theme: "colored" });
    };

    const handleRemoveNewWine = (index) => {
        if (!hasPermission?.("Proposals", "remove")) {
            toast.error("Non hai i permessi per eliminare questo vino.", { theme: "colored" });
            return;
        }
        setNewlySelectedWines((prev) => {
            const itemToRemove = prev[index];
            if (itemToRemove) {
                const wineId = itemToRemove.product_id;
                setSelectedCatalogWineIds((ids) => ids.filter((id) => id !== wineId));
            }
            return prev.filter((_, i) => i !== index);
        });
        toast.info("Vino rimosso dalle nuove selezioni.", { theme: "colored" });
    };

    // Save Handlers
    const handleSaveProposal = async () => {
        if (!hasPermission?.("Proposals", "edit")) {
            toast.error("Non hai i permessi per modificare questa proposal.", { theme: "colored" });
            return;
        }

        const combinedWines = [...existingWines, ...newlySelectedWines];
        if (!combinedWines || combinedWines.length === 0) {
            toast.error("Impossibile salvare una proposal senza vini.", { theme: "colored" });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                wines: combinedWines.map((item) => ({
                    product_id: Number(item.product_id || item.wine?.id),
                    quantity: Math.max(1, parseInt(item.quantity, 10) || 1)
                }))
            };

            const response = await axios.put(`${apiUrl}/api/v1/proposals/${activeProposalId}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const successMsg = response.data?.message || "Proposal aggiornata con successo!";
            toast.success(successMsg, { theme: "colored" });

            const targetProposalId = response.data?.proposal_id || activeProposalId;

            if (onSaveSuccess) {
                onSaveSuccess(response.data);
            }
            if (onClose) {
                onClose();
            } else {
                navigate(`/admin/proposals/${targetProposalId}`);
            }
        } catch (error) {
            console.error("Error saving proposal:", error);
            const msg = error.response?.data?.message || "Errore durante il salvataggio della proposal.";
            toast.error(msg, { theme: "colored" });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (onClose) {
            onClose();
        } else {
            navigate("/admin/proposals");
        }
    };

    // Helper for clean image URLs
    const cleanImageUrl = (url) => {
        if (!url || url === "null") return null;
        let clean = url;
        if (clean.startsWith("[")) {
            clean = clean.split('"]')[0].replace('["', '');
        }
        return clean;
    };

    if (isPageLoading) {
        return (
            <div className="w-full py-12 flex justify-center items-center">
                <table className="w-full">
                    <TableLoader />
                </table>
            </div>
        );
    }

    return (
        <div className="w-full relative space-y-6 pb-10">
            {/* Page Header */}
            <div className="mt-3 mb-2 w-full">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold poppins-medium text-[#303030]">
                    Modifica Proposal {proposal?.restaurant?.company_name ? `- ${proposal.restaurant.company_name}` : ""}
                </h3>
            </div>

            {/* Top Navigation Tabs & Actions Row */}
            <div className="flex flex-wrap items-center justify-between border-b border-[#8B2433]/15 pb-4 mb-2 gap-4">
                {/* Ultra-Premium Segmented Capsule Tabs & Quantity Info Tooltip */}
                <div className="flex items-center gap-3">
                    <div className="inline-flex items-center p-1 bg-[#FAF3F4] border border-[#8B2433]/20 rounded-full shadow-xs">
                        <button
                            type="button"
                            onClick={() => setActiveTab("new_wines")}
                            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm poppins-medium transition-all duration-200 cursor-pointer ${
                                activeTab === "new_wines"
                                    ? "bg-[#8B2433] text-white shadow-md shadow-[#8B2433]/25 font-semibold"
                                    : "text-gray-600 hover:text-[#8B2433] hover:bg-white/60 font-medium"
                            }`}
                        >
                            <RiAddCircleLine className={`w-4 h-4 transition-colors ${activeTab === "new_wines" ? "text-white" : "text-[#8B2433]"}`} />
                            <span>Nuovi Vini</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveTab("existing_wines")}
                            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm poppins-medium transition-all duration-200 cursor-pointer ${
                                activeTab === "existing_wines"
                                    ? "bg-[#8B2433] text-white shadow-md shadow-[#8B2433]/25 font-semibold"
                                    : "text-gray-600 hover:text-[#8B2433] hover:bg-white/60 font-medium"
                            }`}
                        >
                            <RiFileList3Line className={`w-4 h-4 transition-colors ${activeTab === "existing_wines" ? "text-white" : "text-[#8B2433]"}`} />
                            <span>Vini Esistenti</span>
                        </button>
                    </div>

                    <QuantityInfoTooltip />
                </div>

                {/* Right Aligned Action Buttons: Save & Back */}
                <div className="flex items-center gap-3">
                    {Boolean(hasPermission?.("Proposals", "edit")) && (
                        <button
                            type="button"
                            onClick={handleSaveProposal}
                            disabled={isSaving}
                            className="rounded-full py-[6px] border-[2px] bg-[#fff] border-[#F2C3CA] px-5 text-[#8B2433] text-sm font-medium shadow-sm poppins-regular whitespace-nowrap hover:bg-[#8B2433] hover:text-white transition-colors cursor-pointer flex items-center gap-1.5 disabled:opacity-50 group"
                        >
                            <TiTick className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                            <span>{isSaving ? "Salvataggio..." : "Salva"}</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="rounded-full py-[6px] border-[2px] bg-[#fff] border-[#F2C3CA] px-5 text-[#8B2433] text-sm font-medium shadow-sm poppins-regular whitespace-nowrap shrink-0 hover:bg-[#8B2433] hover:text-white transition-colors cursor-pointer"
                    >
                        Torna indietro
                    </button>
                </div>
            </div>

            {/* Component 1: Nuovi Vini Catalog */}
            <NewWinesCatalog
                isVisible={activeTab === "new_wines"}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                dropdownRef={dropdownRef}
                selectedNation={selectedNation}
                setSelectedNation={setSelectedNation}
                handleNationFilter={handleNationFilter}
                nations={nations}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
                handleRegionFilter={handleRegionFilter}
                regions={regions}
                selectedGrape={selectedGrape}
                setSelectedGrape={setSelectedGrape}
                handleGrapeFilter={handleGrapeFilter}
                grapeVarieties={grapeVarieties}
                selectedProductType={selectedProductType}
                setSelectedProductType={setSelectedProductType}
                handleProductTypeFilter={handleProductTypeFilter}
                productTypes={productTypes}
                handleApplyFilters={handleApplyFilters}
                handleResetFilters={handleResetFilters}
                searchNome={searchNome}
                setSearchNome={setSearchNome}
                allWinesData={allWinesData}
                isWinesLoading={isWinesLoading}
                selectedCatalogWineIds={selectedCatalogWineIds}
                existingWineIds={existingWineIds}
                handleToggleSelectCatalogWine={handleToggleSelectCatalogWine}
                handleToggleSelectAllCatalog={handleToggleSelectAllCatalog}
                catalogPage={catalogPage}
                totalCatalogWines={totalCatalogWines}
                catalogRowsPerPage={catalogRowsPerPage}
                setCatalogPage={setCatalogPage}
                setCatalogRowsPerPage={setCatalogRowsPerPage}
                selectedWines={newlySelectedWines}
                handleDirectQuantityInput={handleNewWineQuantityInput}
                handleRemoveWine={handleRemoveNewWine}
                cleanImageUrl={cleanImageUrl}
                canRemove={Boolean(hasPermission?.("Proposals", "remove"))}
            />

            {/* Component 2: Vini Esistenti Order Details */}
            <ExistingWinesOrder
                isVisible={activeTab === "existing_wines"}
                selectedWines={existingWines}
                handleQuantityChange={handleQuantityChange}
                handleDirectQuantityInput={handleDirectQuantityInput}
                handleRemoveWine={handleRemoveWine}
                cleanImageUrl={cleanImageUrl}
                title="Vini Esistenti per la proposta"
                canRemove={Boolean(hasPermission?.("Proposals", "remove"))}
            />
        </div>
    );
};

EditProposal.propTypes = {
    proposalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onClose: PropTypes.func,
    onSaveSuccess: PropTypes.func,
    hasPermission: PropTypes.func
};

export default EditProposal;
